'use client';

import { Typography } from '@workspace/ui/common/Typography/Typography';
import { AppImage } from '@/app/common/Image/AppImage';
import { cn } from '@workspace/lib';
import { Icon } from '@workspace/ui/common';
import { VariantItem } from '../product-variant.types';
import { ResolvedLink } from '@workspace/ui/ResolvedLink';

interface VariantCardProps {
  item: VariantItem;
  compact?: boolean;
}

export function VariantCard({ item, compact }: VariantCardProps) {
  const cardClassName = cn(
    'relative flex flex-row items-stretch rounded-md md:rounded-lg border border-secondary-100 bg-white md:p-0 group',
    compact ? 'h-[132px] md:h-[190px]' : 'h-38.75 md:h-[210px]',
  );

  const link = item.link;
  const hasProductAttribute = !!item.productAttribute;
  const hasLink = !!(link?.href && link?.type);

  const imageUrl = item.image?.asset?.url;
  const imageAlt = item.image?.bynderImage?.alt || item.image?.alt || `${item.heading || 'Variant'} product image`;

  const CardContent = () => (
    <>
      <div className="relative shrink-0 w-[116px] h-auto md:w-[182px] md:h-full lg:w-[210px] rounded-l-md md:rounded-l-lg group bg-transparent">
        {imageUrl ? (
          <AppImage
            src={imageUrl}
            alt={imageAlt}
            className="w-full h-full object-cover rounded-l-md md:rounded-l-lg"
            width={182}
            height={210}
            priority={false}
          />
        ) : (
          <div className="w-full h-full rounded-l-md md:rounded-l-lg" />
        )}
      </div>

      <div className="flex flex-col justify-between m-1 md:m-3 px-2 py-3 md:px-8 md:py-6 lg:px-6 lg:py-6 md:gap-3 overflow-hidden w-full">
        <div className="flex-1 min-h-0">
          <Typography
            variant="h4"
            className="font-bold! text-zinc-700 text-lg! md:mb-1 max-sm:font-normal wrap-break-word line-clamp-2"
            styleVariant="h6"
          >
            {item.heading}
          </Typography>

          {item.subheading && (
            <Typography
              variant="bodySm"
              className="max-sm:text-[12px]! font-openSans text-zinc-600 md:text-[14px]! line-clamp-3!"
            >
              {item.subheading}
            </Typography>
          )}
        </div>

        <div
          className={cn('flex items-center gap-2', hasProductAttribute && hasLink ? 'justify-between' : 'justify-end!')}
        >
          {hasProductAttribute && (
            <Typography variant="bodySm" className="font-sora font-bold!  text-sm!  text-primary md:text-base!">
              {item.productAttribute}
            </Typography>
          )}
          {hasLink && link && (
            <ResolvedLink
              link={{
                ...link,
                url: link.href,
                isOpenInNewTab: link.newTab,
                type: link.type,
              }}
              className={cn(
                'text-sm md:text-base! text-primary hover:text-primary-500 no-underline font-semibold flex items-center gap-2xs',
                link.href ? 'cursor-pointer!' : 'cursor-default ',
              )}
            >
              Learn more
              <Icon
                name="arrowRightIcon"
                size="24"
                className="transition-transform duration-300 ease-in-out group-hover:translate-x-1 text-primary"
              />
            </ResolvedLink>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className={cardClassName}>
      <CardContent />
    </div>
  );
}
